import services from "../../views/Orders/services";

export type InvoiceInfo = {
  invoiceMediaId: string;
  invoiceNo: string;
  invoiceAmount: string;
  invoiceDate: string;
  mrpInvoiceMediaId: string;
};

type KeyMappings = {
  [key in keyof InvoiceInfo]: string;
};

const excludedKeys: Array<keyof InvoiceInfo> = ["invoiceMediaId", "mrpInvoiceMediaId"];

export function transformInvoiceInfoKeys(
  obj: InvoiceInfo
): Record<string, string> {
  const keyMappings: KeyMappings = {
    invoiceMediaId: "Invoice Media Id",
    invoiceNo: "Invoice Number",
    invoiceAmount: "Invoice Amount",
    invoiceDate: "Invoice Date",
    mrpInvoiceMediaId: "MRP Invoice Media Id",
  };

  const transformedObj: Record<string, string> = {};

  for (const key in obj) {
    if (!excludedKeys.includes(key as keyof InvoiceInfo)) {
      if (keyMappings[key as keyof InvoiceInfo]) {
        transformedObj[keyMappings[key as keyof InvoiceInfo]] =
          obj[key as keyof InvoiceInfo];
      } else {
        transformedObj[key] = obj[key as keyof InvoiceInfo];
      }
    }
  }

  return transformedObj;
}

export type OrderItem = {
  partId: string;
  rootPartNumber: string;
  orderedQuantity: number;
  [key: string]: any;
};

export type SupplierParts = {
  rootPartNumber: string;
  familyQuantity: number;
  [key: string]: any;
};

export type OrderItemDiff = {
  fulfilledOrderItems: OrderItem[];
  remainingOrderItems: OrderItem[];
};

export function splitOrderItemsBySupplierInventory(
  orderItems: OrderItem[],
  supplierParts: SupplierParts[]
): OrderItemDiff {
  const supplierInventoryMap = new Map<string, number>();
  supplierParts.forEach((supplierPart) => {
    supplierInventoryMap.set(
      supplierPart.rootPartNumber,
      supplierPart.familyQuantity
    );
  });

  const fulfilledOrderItems: OrderItem[] = [];
  const remainingOrderItems: OrderItem[] = [];

  for (const item of orderItems) {
    const requestedQuantity = item.remainingDecisionQuantity;
    const availableQuantity =
      supplierInventoryMap.get(item.rootPartNumber) ?? 0;

    const fulfilledQuantity = Math.min(requestedQuantity, availableQuantity);
    const remainingQuantity = Math.max(
      0,
      requestedQuantity - availableQuantity
    );

    fulfilledOrderItems.push({
      ...item,
      orderedQuantity: fulfilledQuantity,
      remainingDecisionQuantity: fulfilledQuantity,
      holdQuantity: 0,
      cancelledQuantity: 0,
    });

    remainingOrderItems.push({
      ...item,
      orderedQuantity: remainingQuantity,
      remainingDecisionQuantity: remainingQuantity,
      holdQuantity: 0,
      cancelledQuantity: 0,
    });
  }

  return {
    fulfilledOrderItems: fulfilledOrderItems.filter(
      (item) => item.orderedQuantity !== 0
    ),
    remainingOrderItems: remainingOrderItems.filter(
      (item) => item.orderedQuantity !== 0
    ),
  };
}

export type OrderMetaInfo = {
  orderHandlerAllocationTime: string;
  [key: string]: any;
};

export async function downloadInvoiceForOrder(invoiceMediaId: any) {
  try {
    const mediaResponse = await services.getMediaData(invoiceMediaId);
    window.open(mediaResponse.path, '_blank')
  } catch (error) {
    alert("Invoice is not available");
  }
}


export function getEnquiryStatus(
  orderItems: any[],
  orderStatus: string
): "OPEN" | "CLOSED" {
  if (orderStatus === "CANCELLED") {
    return "CLOSED";
  }

  if (orderStatus === "CUSTOMER_HOLD") {
    return "OPEN";
  }

  if (!orderItems || orderItems.length === 0) {
    return "OPEN";
  }

  const totalRemaining = orderItems.reduce((acc, item) => {
    return (
      acc + (item.remainingDecisionQuantity || 0) + (item.holdQuantity || 0)
    );
  }, 0);

  return totalRemaining === 0 ? "CLOSED" : "OPEN";
}
